/**
 * Role-Change Session Watcher (Feb 27, 2026).
 *
 * Lightweight background poll that detects out-of-band role/permission
 * changes for the currently-logged-in user and forces a clean re-login.
 *
 * Why this exists:
 * When an admin changes a user's role via /api/admin/users/{id}/roles,
 * the backend stamps `role_changed_at` on the user document. The next
 * request the affected user makes to a guarded endpoint returns 401
 * with `X-Logout-Reason: role_changed` + detail "Your role was
 * updated. Please log in again." — and the global axios interceptor
 * (App.js) clears caches + redirects.
 *
 * BUT — if the user has the app open in a background tab and isn't
 * actively interacting, that next request might not happen for minutes
 * or hours. This watcher closes that gap by issuing a cheap GET
 * /auth/me at a low cadence ONLY when the tab is visible.
 *
 * Cost profile (per active tab):
 *   - Initial check: 1 request on mount
 *   - Background:    1 request every POLL_INTERVAL_MS (default 60s)
 *                    ONLY while document.visibilityState === 'visible'
 *   - Focus boost:   1 immediate request when the tab regains focus
 *                    (debounced to once every MIN_FOCUS_GAP_MS)
 *
 * The watcher is purely passive — it never mutates state on its own.
 * Detection happens via:
 *   - Axios interceptor catching 401 with detail "role was updated"
 *     (already wired in App.js) → forced logout + toast
 *   - Direct comparison of returned `role` vs the user's current role
 *     in React state (paranoid double-check in case backend serves
 *     200 with refreshed role but no 401 — e.g. token re-issued by
 *     another tab)
 *
 * Impersonation safety:
 *   - The watcher uses whatever bearer token axios currently has,
 *     so during impersonation it polls /auth/me for the impersonated
 *     user. If the impersonator's authority is revoked or the
 *     impersonated user is locked, the standard 401 flow kicks in.
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { dedupedGet } from '../lib/requestDedupe';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const POLL_INTERVAL_MS = 60 * 1000;   // 60s — quiet enough to be invisible at scale
const MIN_FOCUS_GAP_MS = 15 * 1000;   // never refire focus-check sooner than 15s

/**
 * @param {object|null} user           current React-state user (must include id + role)
 * @param {function}    onRoleChange   callback fired when a role mismatch is detected;
 *                                     should clear caches + force logout.
 *                                     The 401 path already does this; this callback
 *                                     covers the rare 200-with-different-role case.
 */
export const useRoleChangeWatcher = (user, onRoleChange) => {
  const lastCheckTsRef = useRef(0);
  const inFlightRef = useRef(false);
  const userRef = useRef(user);

  // Keep the latest user in a ref so the interval/visibility handlers
  // always compare against the current value without restarting timers.
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const performCheck = async (source) => {
      // Cheap dedup — bail if a request is already in flight or the
      // last completed check was very recent.
      if (inFlightRef.current) return;
      if (Date.now() - lastCheckTsRef.current < 5000) return;
      if (document.visibilityState === 'hidden') return;

      inFlightRef.current = true;
      try {
        // The axios interceptor in App.js handles 401 + role_changed
        // automatically; we just need the request to happen.
        const resp = await dedupedGet(`${API}/auth/me`);
        const serverRole = resp.data?.role;
        const currentRole = userRef.current?.role;
        if (
          serverRole &&
          currentRole &&
          serverRole !== currentRole
        ) {
          // 200 OK but role mismatch — rare path (e.g. a fresh token was
          // issued elsewhere with the new role before the old one expired).
          // Surface the same friendly logout flow.
          console.warn(
            `[RoleChangeWatcher] role mismatch detected via ${source}: ` +
            `server="${serverRole}" current="${currentRole}"`
          );
          try {
            toast.info?.('Your role was updated. Please log in again.');
          } catch (_) { /* sonner not loaded */ }
          onRoleChange?.();
        }
      } catch (_err) {
        // Errors are intentionally swallowed — the global axios
        // interceptor already handles 401 / role_changed. Network
        // hiccups during this background poll should be invisible.
      } finally {
        inFlightRef.current = false;
        lastCheckTsRef.current = Date.now();
      }
    };

    // One immediate check on mount (catches role changes that happened
    // while the tab was closed/hidden).
    performCheck('mount');

    // Periodic poll (cheap; skipped automatically when tab is hidden).
    const intervalId = setInterval(() => performCheck('interval'), POLL_INTERVAL_MS);

    // Visibility-change boost — when the user tabs back in, do an
    // immediate check (debounced to MIN_FOCUS_GAP_MS).
    let lastFocusCheckTs = 0;
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusCheckTs < MIN_FOCUS_GAP_MS) return;
      lastFocusCheckTs = now;
      performCheck('visibility');
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // We intentionally key the effect off user?.id only — the polling
    // loop stays alive across role refreshes within the same session.
  }, [user?.id]);
};
